import jsPDF from "jspdf";
import { WorkflowManifest } from "../../api/workflows";

// PDF rendering for the workflow manifest. Same brand palette /
// chrome pattern as searchEnv/handoffRender.ts so a customer who
// receives both artefacts gets a coherent TVEyes-branded set.
//
// Layout (Letter, portrait) — each section gets its own page so a
// customer can print + hand off individual sections without partial-
// page splits:
//   Page 1  — Cover: scope headline, filter, output info
//   Page 2  — Top stations
//   Page 3  — Top program categories
//   Page 4  — Time distribution (adaptively bucketed: day / week /
//             month so high-volume exports don't render hundreds of
//             pixel-wide bars)
//   Page 5  — Per-month breakdown (multi-month workflows only)

const PAGE_W = 612;
const MARGIN_X = 54;
const BODY_TOP = 110;
const BODY_BOTTOM = 742;
const FOOTER_Y = 762;

const COLOR_PRIMARY: [number, number, number] = [40, 109, 178];
const COLOR_SECONDARY: [number, number, number] = [120, 168, 66];
const COLOR_INK: [number, number, number] = [4, 36, 65];
const COLOR_MUTED: [number, number, number] = [120, 130, 145];
const COLOR_RULE: [number, number, number] = [225, 230, 238];
const COLOR_BAR: [number, number, number] = [40, 109, 178];

interface PdfState {
  doc: jsPDF;
  y: number;
  page: number;
  workflowIdLabel: string;
}

function applyRgb(
  setter: (r: number, g: number, b: number) => unknown,
  rgb: [number, number, number],
): void {
  setter(rgb[0], rgb[1], rgb[2]);
}

function pageChrome(state: PdfState, isFirstPage: boolean): void {
  const { doc } = state;
  applyRgb(doc.setFillColor.bind(doc), COLOR_PRIMARY);
  doc.rect(0, 0, PAGE_W, 6, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.text("TVEyes • Export Manifest", MARGIN_X, 36);
  doc.text(state.workflowIdLabel, PAGE_W - MARGIN_X, 36, { align: "right" });
  applyRgb(doc.setDrawColor.bind(doc), COLOR_RULE);
  doc.setLineWidth(0.5);
  doc.line(MARGIN_X, FOOTER_Y - 14, PAGE_W - MARGIN_X, FOOTER_Y - 14);
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFontSize(8);
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(`Generated ${today}`, MARGIN_X, FOOTER_Y);
  doc.text(`Page ${state.page}`, PAGE_W - MARGIN_X, FOOTER_Y, { align: "right" });
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  applyRgb(doc.setDrawColor.bind(doc), COLOR_INK);
  doc.setLineWidth(0.5);
  if (isFirstPage) drawHero(state);
}

function drawHero(state: PdfState): void {
  const { doc } = state;
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.text("Export Manifest", MARGIN_X, 64);
  applyRgb(doc.setFillColor.bind(doc), COLOR_SECONDARY);
  doc.rect(MARGIN_X, 70, 44, 3, "F");
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(
    "Pre-aggregated counters for the data delivered to your output prefix.",
    MARGIN_X,
    90,
  );
  state.y = BODY_TOP;
}

function ensureSpace(state: PdfState, needed: number): void {
  if (state.y + needed > BODY_BOTTOM) {
    forcePageBreak(state);
  }
}

// Start a fresh page (no hero — only page 1 gets the hero).
function forcePageBreak(state: PdfState): void {
  state.doc.addPage();
  state.page += 1;
  pageChrome(state, false);
  state.y = BODY_TOP;
}

// Begin a new SECTION page: force a page break, draw a section title
// + an explanatory paragraph that tells the reader what they're
// about to see. Every non-cover section uses this so the customer
// can flip through "what is this counter, how do I read it" without
// hunting through dense tables for context.
function section(state: PdfState, title: string, description: string): void {
  forcePageBreak(state);
  const { doc } = state;
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(title, MARGIN_X, state.y);
  state.y += 8;
  applyRgb(doc.setFillColor.bind(doc), COLOR_SECONDARY);
  doc.rect(MARGIN_X, state.y, 36, 2, "F");
  state.y += 20;
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const lines = doc.splitTextToSize(description, PAGE_W - 2 * MARGIN_X);
  for (const line of lines) {
    doc.text(line, MARGIN_X, state.y);
    state.y += 13;
  }
  state.y += 14;
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
}

function kvRow(state: PdfState, label: string, value: string): void {
  ensureSpace(state, 16);
  const { doc } = state;
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(label, MARGIN_X, state.y);
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  doc.setFont("helvetica", "bold");
  // Available width for the value: from the value column (label + 140)
  // to the right body margin (PAGE_W - MARGIN_X). Previously this was
  // miscalculated by 34pt and long Output URIs would clip into the
  // right margin before wrapping.
  const valueX = MARGIN_X + 140;
  const valueMaxW = PAGE_W - MARGIN_X - valueX;
  const lines = doc.splitTextToSize(value, valueMaxW);
  for (let i = 0; i < lines.length; i++) {
    if (i > 0) {
      ensureSpace(state, 14);
    }
    doc.text(lines[i], valueX, state.y);
    state.y += 14;
  }
}

// Hero block on the cover page: big total + scope chip. Replaces the
// old tinted ``calloutBlock`` which read as a banner advert rather
// than a customer-facing scope summary.
function drawHeroScope(
  state: PdfState,
  m: WorkflowManifest,
  unit: string,
): void {
  const { doc } = state;
  state.y += 4;
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(
    `TOTAL ${unit.toUpperCase()}S MATCHED`,
    PAGE_W / 2,
    state.y,
    { align: "center" },
  );
  state.y += 36;
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(40);
  doc.text(fmtNumber(m.totals.total), PAGE_W / 2, state.y, { align: "center" });
  state.y += 24;

  // Time-range subtitle. Only render the bound(s) we actually have;
  // the older renderer printed "? to ?" when the C# manifest didn't
  // populate either side.
  const tr = m.time_range;
  let tlabel: string | null = null;
  if (tr?.min_start_utc && tr?.max_end_utc) {
    tlabel = `${fmtIsoForPdf(tr.min_start_utc)}  →  ${fmtIsoForPdf(tr.max_end_utc)}`;
  } else if (tr?.min_start_utc) {
    tlabel = `from ${fmtIsoForPdf(tr.min_start_utc)}`;
  } else if (tr?.max_end_utc) {
    tlabel = `until ${fmtIsoForPdf(tr.max_end_utc)}`;
  }
  if (tlabel) {
    applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(tlabel, PAGE_W / 2, state.y, { align: "center" });
    state.y += 18;
  }
  state.y += 8;
}

// Equal-width tile row of secondary scope metrics. Renders 2-4 tiles
// depending on what's available — ``Pages`` only when the record-count
// and page-count differ, ``Transcript text`` only when a sink actually
// populated transcript_chars (condensed-json today; the other sinks
// leave it at 0 and the tile would always read "0 B").
function drawTileRow(
  state: PdfState,
  tiles: { label: string; value: string }[],
): void {
  if (tiles.length === 0) return;
  const { doc } = state;
  const tileH = 56;
  const gap = 10;
  const totalW = PAGE_W - 2 * MARGIN_X;
  const tileW = (totalW - gap * (tiles.length - 1)) / tiles.length;
  ensureSpace(state, tileH + 4);
  for (let i = 0; i < tiles.length; i++) {
    const x = MARGIN_X + i * (tileW + gap);
    const y = state.y;
    applyRgb(doc.setDrawColor.bind(doc), COLOR_RULE);
    doc.setLineWidth(0.6);
    doc.roundedRect(x, y, tileW, tileH, 3, 3, "S");
    applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(tiles[i].label.toUpperCase(), x + tileW / 2, y + 17, { align: "center" });
    applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(tiles[i].value, x + tileW / 2, y + 42, { align: "center" });
  }
  state.y += tileH + 8;
}

// In-body section header (smaller than the per-page ``section`` title).
// Used on the cover to break Delivery and Query into their own labelled
// blocks without consuming a whole page.
function drawCoverSectionHeader(state: PdfState, title: string): void {
  const { doc } = state;
  ensureSpace(state, 24);
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(title, MARGIN_X, state.y);
  state.y += 4;
  applyRgb(doc.setFillColor.bind(doc), COLOR_SECONDARY);
  doc.rect(MARGIN_X, state.y, 24, 2, "F");
  state.y += 14;
}

// Monospace code block used for filter queries on the cover. The
// header strip carries the label ("Filter" / "Lucene" / "Regex");
// the body wraps the query text in courier so customers can see the
// exact predicate that produced the data.
function drawCodeBlock(state: PdfState, label: string, text: string): void {
  const { doc } = state;
  doc.setFont("courier", "normal");
  doc.setFontSize(9);
  const blockW = PAGE_W - 2 * MARGIN_X;
  const lines = doc.splitTextToSize(text, blockW - 16);
  const lineH = 12;
  const padding = 6;
  const headerH = 18;
  const bodyH = lines.length * lineH + padding * 2;
  const blockH = headerH + bodyH;
  ensureSpace(state, blockH + 10);
  const top = state.y;
  applyRgb(doc.setFillColor.bind(doc), COLOR_RULE);
  doc.rect(MARGIN_X, top, blockW, headerH, "F");
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(label.toUpperCase(), MARGIN_X + 8, top + 13);
  applyRgb(doc.setDrawColor.bind(doc), COLOR_RULE);
  doc.setLineWidth(0.5);
  doc.rect(MARGIN_X, top + headerH, blockW, bodyH, "S");
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  doc.setFont("courier", "normal");
  doc.setFontSize(9);
  for (let i = 0; i < lines.length; i++) {
    doc.text(lines[i], MARGIN_X + 8, top + headerH + padding + (i + 1) * lineH - 3);
  }
  state.y = top + blockH + 10;
}

function fmtNumber(n: number): string {
  return n.toLocaleString();
}

function fmtHoursFromSeconds(sec: number): string {
  const hours = sec / 3600;
  if (hours >= 1000) return `${Math.round(hours).toLocaleString()} hours`;
  if (hours >= 10) return `${hours.toFixed(1)} hours`;
  return `${hours.toFixed(2)} hours`;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// Trim an ISO-8601 timestamp down to ``YYYY-MM-DD HH:MM:SS UTC`` for
// PDF rendering. The C# exporter writes timestamps with microsecond
// precision (``2026-05-06T15:59:06.131001``); microseconds aren't
// useful in a human-readable manifest and they eat horizontal space.
function fmtIsoForPdf(s: string | undefined): string {
  if (!s) return "?";
  // Tolerate "YYYY-MM-DDTHH:MM:SS[.fff]" with optional trailing "Z".
  const m = s.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/);
  if (!m) return s;
  return `${m[1]} ${m[2]} UTC`;
}

// Bucket the by_day distribution to keep PDF bars readable. Hundreds
// of pixel-wide day-bars are noise; weekly buckets for ~2-12 months
// of data, monthly buckets for multi-year. Returns the bucketed
// rows + a human-readable label for the unit.
function bucketHistogram(
  rows: { date: string; count: number }[],
): { rows: { label: string; count: number }[]; unit: string } {
  if (rows.length === 0) return { rows: [], unit: "day" };
  if (rows.length <= 60) {
    return {
      rows: rows.map((r) => ({ label: r.date, count: r.count })),
      unit: "day",
    };
  }
  if (rows.length <= 365 * 2) {
    // Weekly buckets (Mon-anchored ISO weeks). Walk in order and
    // group rows whose date falls in the same week.
    const out = new Map<string, number>();
    for (const r of rows) {
      const d = new Date(r.date + "T00:00:00Z");
      // Roll back to Monday of that week.
      const dow = (d.getUTCDay() + 6) % 7; // 0=Mon
      const monday = new Date(d);
      monday.setUTCDate(d.getUTCDate() - dow);
      const key = monday.toISOString().slice(0, 10);
      out.set(key, (out.get(key) ?? 0) + r.count);
    }
    return {
      rows: Array.from(out, ([label, count]) => ({ label, count })),
      unit: "week",
    };
  }
  // Monthly buckets — multi-year exports.
  const out = new Map<string, number>();
  for (const r of rows) {
    const key = r.date.slice(0, 7); // YYYY-MM
    out.set(key, (out.get(key) ?? 0) + r.count);
  }
  return {
    rows: Array.from(out, ([label, count]) => ({ label, count })),
    unit: "month",
  };
}

// Pick a "nice" y-axis ceiling at or just above ``rawMax`` so the
// gridline numbers are readable round figures (10, 25, 50, 100, ...)
// instead of arbitrary peaks like "1,751". Returns the chosen ceiling
// plus the four gridline values at 0/25/50/75/100% of it.
function niceYTicks(rawMax: number): { ceiling: number; ticks: number[] } {
  if (rawMax <= 0) return { ceiling: 1, ticks: [1, 0.75, 0.5, 0.25, 0] };
  const mag = Math.pow(10, Math.floor(Math.log10(rawMax)));
  const norm = rawMax / mag;
  let nice: number;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 2.5) nice = 2.5;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  const ceiling = nice * mag;
  return {
    ceiling,
    ticks: [ceiling, ceiling * 0.75, ceiling * 0.5, ceiling * 0.25, 0],
  };
}

function drawHistogram(
  state: PdfState,
  rows: { label: string; count: number }[],
): void {
  if (rows.length === 0) {
    const { doc } = state;
    applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
    doc.setFontSize(10);
    doc.text("(no time-stamped records)", MARGIN_X, state.y);
    state.y += 14;
    return;
  }
  const { doc } = state;
  const rawMax = rows.reduce((m, r) => Math.max(m, r.count), 0);
  const { ceiling, ticks } = niceYTicks(rawMax);

  // Leave room on the left for the y-axis tick labels.
  const YAXIS_W = 40;
  const chartLeft = MARGIN_X + YAXIS_W;
  const chartRight = PAGE_W - MARGIN_X;
  const chartW = chartRight - chartLeft;
  const barAreaH = 200;
  ensureSpace(state, barAreaH + 36);
  const y0 = state.y;

  // Horizontal gridlines at the tick values + their labels on the left.
  applyRgb(doc.setDrawColor.bind(doc), COLOR_RULE);
  doc.setLineWidth(0.5);
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFontSize(8);
  for (const t of ticks) {
    const yLine = y0 + barAreaH - (t / ceiling) * barAreaH;
    doc.line(chartLeft, yLine, chartRight, yLine);
    doc.text(fmtNumber(Math.round(t)), chartLeft - 4, yLine + 3, { align: "right" });
  }

  // Bars on top of the gridlines so the gridlines read through gaps.
  const barW = chartW / Math.max(rows.length, 1);
  applyRgb(doc.setFillColor.bind(doc), COLOR_BAR);
  for (let i = 0; i < rows.length; i++) {
    const h = (rows[i].count / ceiling) * barAreaH;
    doc.rect(chartLeft + i * barW + 1, y0 + barAreaH - h, Math.max(barW - 2, 1), h, "F");
  }

  state.y += barAreaH + 6;
  // Endpoint labels under the chart.
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFontSize(8);
  doc.text(rows[0].label, chartLeft, state.y);
  doc.text(rows[rows.length - 1].label, chartRight, state.y, { align: "right" });
  state.y += 12;
  // Peak callout below the endpoints.
  const peakIdx = rows.findIndex((r) => r.count === rawMax);
  if (peakIdx >= 0) {
    applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
      `Peak: ${rows[peakIdx].label} (${fmtNumber(rawMax)})`,
      chartLeft,
      state.y + 8,
    );
    state.y += 20;
  }
}

function drawRankTable(
  state: PdfState,
  rows: { name: string; count: number }[],
): void {
  const { doc } = state;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  // Header row
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Name", MARGIN_X, state.y);
  doc.text("Count", PAGE_W - MARGIN_X, state.y, { align: "right" });
  state.y += 6;
  applyRgb(doc.setDrawColor.bind(doc), COLOR_RULE);
  doc.line(MARGIN_X, state.y, PAGE_W - MARGIN_X, state.y);
  state.y += 14;
  // Rows
  applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  for (const r of rows) {
    ensureSpace(state, 16);
    const label = r.name.length > 60 ? r.name.slice(0, 58) + "…" : r.name;
    doc.text(label, MARGIN_X, state.y);
    applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
    doc.text(fmtNumber(r.count), PAGE_W - MARGIN_X, state.y, { align: "right" });
    applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
    state.y += 14;
  }
}

function drawMonthTable(
  state: PdfState,
  months: WorkflowManifest["months"],
): void {
  const { doc } = state;
  // Header row
  applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Month", MARGIN_X, state.y);
  doc.text("Records", MARGIN_X + 110, state.y, { align: "right" });
  doc.text("Pages", MARGIN_X + 200, state.y, { align: "right" });
  doc.text("Hours", MARGIN_X + 290, state.y, { align: "right" });
  doc.text("Transcript", PAGE_W - MARGIN_X, state.y, { align: "right" });
  state.y += 6;
  applyRgb(doc.setDrawColor.bind(doc), COLOR_RULE);
  doc.line(MARGIN_X, state.y, PAGE_W - MARGIN_X, state.y);
  state.y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  for (const me of months) {
    ensureSpace(state, 14);
    applyRgb(doc.setTextColor.bind(doc), COLOR_INK);
    doc.text(me.month ?? "(workflow)", MARGIN_X, state.y);
    if (me.manifest) {
      doc.text(
        fmtNumber(me.manifest.matched.total),
        MARGIN_X + 110,
        state.y,
        { align: "right" },
      );
      doc.text(
        fmtNumber(me.manifest.matched.total_page_count),
        MARGIN_X + 200,
        state.y,
        { align: "right" },
      );
      doc.text(
        fmtHoursFromSeconds(me.manifest.matched.total_duration_sec),
        MARGIN_X + 290,
        state.y,
        { align: "right" },
      );
      doc.text(
        fmtBytes(me.manifest.matched.total_transcript_chars),
        PAGE_W - MARGIN_X,
        state.y,
        { align: "right" },
      );
    } else {
      applyRgb(doc.setTextColor.bind(doc), COLOR_MUTED);
      doc.text("(manifest not found)", MARGIN_X + 110, state.y);
    }
    state.y += 14;
  }
}

export function renderManifestPdf(m: WorkflowManifest): jsPDF {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const state: PdfState = {
    doc,
    y: BODY_TOP,
    page: 1,
    workflowIdLabel: m.workflow_id.slice(0, 16),
  };
  pageChrome(state, true);

  // ============= PAGE 1 — Cover =============
  const unit = m.output.unit || "record";

  // Headline: big number + caption + time range subtitle.
  drawHeroScope(state, m, unit);

  // Secondary scope metrics, equal-width tiles.
  const tiles: { label: string; value: string }[] = [
    {
      label: "Stations",
      value: fmtNumber(m.totals.distinct_stations_seen),
    },
    {
      label: "Content captured",
      value: fmtHoursFromSeconds(m.totals.total_duration_sec),
    },
  ];
  if (m.totals.total !== m.totals.total_page_count) {
    tiles.push({
      label: "Pages",
      value: fmtNumber(m.totals.total_page_count),
    });
  }
  if (m.totals.total_transcript_chars > 0) {
    tiles.push({
      label: "Transcript text",
      value: fmtBytes(m.totals.total_transcript_chars),
    });
  }
  drawTileRow(state, tiles);
  state.y += 8;

  // Delivery: where the data lives + who asked for it.
  drawCoverSectionHeader(state, "Delivery");
  kvRow(state, "Output", m.output.uri);
  kvRow(state, "Format", `${m.output.format ?? "?"} (one record per ${unit})`);
  if (m.submitted_by) kvRow(state, "Submitted by", m.submitted_by);
  if (m.submitted_at) {
    kvRow(state, "Submitted at", new Date(m.submitted_at).toUTCString());
  }
  if (m.totals.months_with_hits + m.totals.months_empty > 1) {
    kvRow(
      state,
      "Coverage",
      `${m.totals.months_with_hits} month${m.totals.months_with_hits === 1 ? "" : "s"} with hits` +
        (m.totals.months_empty
          ? ` · ${m.totals.months_empty} empty`
          : "") +
        (m.totals.months_missing_manifest
          ? ` · ${m.totals.months_missing_manifest} missing manifest`
          : ""),
    );
  }
  state.y += 12;

  // Query: the predicate(s) that produced the data. Monospaced code
  // blocks rather than kv rows since these can wrap to multiple lines.
  if (
    m.filter &&
    (m.filter.expression || m.filter.lucene_query || m.filter.transcript_regex)
  ) {
    drawCoverSectionHeader(state, "Query");
    if (m.filter.expression) drawCodeBlock(state, "Filter", m.filter.expression);
    if (m.filter.lucene_query) drawCodeBlock(state, "Lucene", m.filter.lucene_query);
    if (m.filter.transcript_regex)
      drawCodeBlock(state, "Regex", m.filter.transcript_regex);
  }

  // ============= PAGE 2 — Top stations =============
  if (m.by_station.length > 0) {
    section(
      state,
      "Top stations",
      "Stations that contributed records to this export, ranked by count. " +
        "Each row shows a station name and how many " + unit + "s came from it. " +
        "Limited to the top 20 stations across the matched data; the manifest's parquet sidecar " +
        "(_index.parquet, in the same output prefix) carries the full station list for ad-hoc " +
        "DuckDB queries.",
    );
    drawRankTable(
      state,
      m.by_station.map((r) => ({ name: r.source_name, count: r.count })),
    );
  }

  // ============= PAGE 3 — Top categories =============
  if (m.by_category.length > 0) {
    section(
      state,
      "Top program categories",
      "Programs in this export grouped by category (News, Sports, Talk, etc.), " +
        "ranked by record count. Categories are assigned by the upstream " +
        "broadcast classification — multi-category programs land in their primary " +
        "category only. Top 10 shown; the parquet sidecar carries every category present.",
    );
    drawRankTable(
      state,
      m.by_category.map((r) => ({ name: r.category, count: r.count })),
    );
  }

  // ============= PAGE 4 — Time distribution =============
  if (m.by_day.length > 0) {
    const bucketed = bucketHistogram(m.by_day);
    section(
      state,
      "Time distribution",
      `Records grouped by ${bucketed.unit} of broadcast start time. ` +
        `Bar height is proportional to record count; the tallest bar marks the ${bucketed.unit} ` +
        `with the most activity. ${
          bucketed.unit === "day"
            ? "Each bar is one calendar day."
            : bucketed.unit === "week"
              ? "Days have been bucketed into ISO weeks (Monday-anchored) because the export spans more than 60 days."
              : "Days have been bucketed into calendar months because the export spans multiple years."
        }`,
    );
    drawHistogram(state, bucketed.rows);
  }

  // ============= PAGE 5 — Per-month breakdown =============
  if (m.months.length > 1) {
    section(
      state,
      "Per-month breakdown",
      "Each row corresponds to one month subdirectory in the output prefix " +
        "(<output>/YYYY-MM/). Counts are pulled from that month's own _manifest.json " +
        "sidecar; months with zero matches still have a manifest written but produce " +
        "a 0-byte data file. \"Records\" is the count in the manifest's unit (page or " +
        "superdoc); \"Pages\" is always the page-level count regardless of unit.",
    );
    drawMonthTable(state, m.months);
  }

  return doc;
}
